import { Head, Link, router } from '@inertiajs/react'
import { useCallback, useState } from 'react'
import { HiOutlineInformationCircle, HiOutlinePencil, HiOutlineSparkles, HiOutlineTrash } from 'react-icons/hi2'
import AppLayout from '../../components/AppLayout'
import ConfirmDialog from '../../components/ConfirmDialog'
import InfoDialog from '../../components/InfoDialog'
import ScoringExplainer from '../../components/ScoringExplainer'

type OptionSummary = { label: string; score: number; lead_count: number }

type Template = {
  id: number
  text: string
  description: string
  question_type: 'single' | 'multi'
  option_count: number
  options: OptionSummary[]
  event_count: number
  lead_count: number
  updated_at_display: string
}

type Suggestion = {
  label: string
  score: number
  reason: string
}

type SuggestResponse = {
  won_leads: number
  total_leads: number
  events: number
  suggestions: Suggestion[]
  insights: string[]
  summary: string
}

type Props = {
  templates: Template[]
}

type CardSuggestState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: SuggestResponse }
  | { status: 'error'; message: string }

export default function QuestionTemplatesIndex({ templates }: Props) {
  const [confirming, setConfirming] = useState<Template | null>(null)
  const [scoringInfoOpen, setScoringInfoOpen] = useState(false)
  const [applying, setApplying] = useState(false)

  // Per-card suggestion state keyed by template id
  const [suggestStates, setSuggestStates] = useState<Record<number, CardSuggestState>>({})
  // Which template's modal is currently open
  const [viewingId, setViewingId] = useState<number | null>(null)

  const getState = (id: number): CardSuggestState => suggestStates[id] || { status: 'idle' }

  const updateState = (id: number, state: CardSuggestState) =>
    setSuggestStates((prev) => ({ ...prev, [id]: state }))

  const handleDelete = () => {
    if (!confirming) return
    router.delete(`/question_templates/${confirming.id}`, {
      onFinish: () => setConfirming(null),
    })
  }

  const fetchSuggestions = useCallback(async (templateId: number) => {
    updateState(templateId, { status: 'loading' })
    try {
      const res = await fetch(`/question_templates/${templateId}/suggest_scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json()
      if (!res.ok) {
        updateState(templateId, { status: 'error', message: data.error || 'Something went wrong' })
      } else {
        updateState(templateId, { status: 'ready', data })
      }
    } catch {
      updateState(templateId, { status: 'error', message: 'Network error — please try again' })
    }
  }, [])

  const applySuggestions = useCallback(async () => {
    if (viewingId === null) return
    const state = getState(viewingId)
    if (state.status !== 'ready') return
    setApplying(true)
    const scores: Record<string, number> = {}
    state.data.suggestions.forEach((s) => { scores[s.label] = s.score })
    try {
      await fetch(`/question_templates/${viewingId}/apply_scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores }),
      })
      updateState(viewingId, { status: 'idle' })
      setViewingId(null)
      router.reload()
    } finally {
      setApplying(false)
    }
  }, [viewingId, suggestStates])

  const viewingState = viewingId !== null ? getState(viewingId) : null
  const viewingTemplate = viewingId !== null ? templates.find((t) => t.id === viewingId) : null

  return (
    <>
      <Head title="Question Templates" />
      <AppLayout active="question_templates">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink md:text-[40px] md:leading-[1.2]">
              Question Templates
            </h1>
            <p className="mt-2 font-display text-base text-black/60">
              Reusable questions you can drop into any event.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setScoringInfoOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-4 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
            >
              <HiOutlineInformationCircle className="h-4 w-4" />
              How scoring works
            </button>
            <Link
              href="/question_templates/new"
              className="inline-flex items-center justify-center rounded-md bg-ink px-8 py-4 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green"
            >
              Create Template
            </Link>
          </div>
        </div>

        <div className="mt-6">
          <h2 className="font-display text-base font-medium text-black/60">
            Templates ({templates.length})
          </h2>

          {templates.length === 0 ? (
            <div className="mt-4 rounded-xl bg-white p-12 text-center font-display text-base text-black/50 shadow-sm">
              No templates yet. Create one to get started.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((t) => (
                <div
                  key={t.id}
                  className="flex flex-col gap-3 rounded-xl bg-white p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <h3 className="font-display text-lg font-medium text-ink">{t.text}</h3>
                      <span className={`inline-flex w-fit items-center rounded-full px-3 py-0.5 font-display text-xs font-medium ${
                        t.question_type === 'single'
                          ? 'bg-[#93D500]/20 text-midnight-green'
                          : 'bg-[#00BED6]/20 text-midnight-green'
                      }`}>
                        {t.question_type === 'single' ? 'Single choice' : 'Multi choice'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link
                        href={`/question_templates/${t.id}/edit`}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50"
                        aria-label={`Edit ${t.text}`}
                      >
                        <HiOutlinePencil className="h-5 w-5" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => setConfirming(t)}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-rose-50 hover:text-rose-600"
                        aria-label={`Delete ${t.text}`}
                      >
                        <HiOutlineTrash className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  {t.description && (
                    <p className="font-display text-sm text-black/50">{t.description}</p>
                  )}

                  {(t.event_count > 0 || t.lead_count > 0) && (
                    <div className="flex gap-3 font-display text-xs text-black/40">
                      <span>{t.event_count} event{t.event_count === 1 ? '' : 's'}</span>
                      <span className="text-black/20">|</span>
                      <span>{t.lead_count} lead{t.lead_count === 1 ? '' : 's'}</span>
                    </div>
                  )}

                  {t.options.length > 0 && (
                    <div>
                      <div className="mb-0.5 flex items-center gap-2 font-display text-[10px] font-medium uppercase tracking-wide text-black/25">
                        <span className="min-w-0 flex-1">Option</span>
                        <span className="shrink-0">Leads</span>
                        <span className="w-5 shrink-0 text-right">Score</span>
                      </div>
                      <ul className="flex flex-col gap-0.5">
                        {t.options.map((o) => (
                          <li key={o.label} className="flex items-center gap-2 font-display text-xs">
                            <span className="min-w-0 flex-1 truncate text-black/50">{o.label}</span>
                            <span className="shrink-0 tabular-nums text-black/30">{o.lead_count}</span>
                            {o.score > 0 ? (
                              <span className="w-5 shrink-0 text-right tabular-nums font-medium text-midnight-green">{o.score}</span>
                            ) : (
                              <span className="w-5 shrink-0 text-right tabular-nums text-black/20">-</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-auto flex items-center justify-between pt-1">
                    <SuggestButton state={getState(t.id)} onSuggest={() => fetchSuggestions(t.id)} onView={() => setViewingId(t.id)} />
                    <span className="font-display text-xs text-black/40">
                      Updated {t.updated_at_display}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AppLayout>

      <ConfirmDialog
        open={confirming !== null}
        tone="danger"
        title="Delete this template?"
        body={
          <>
            Deleting <strong>{confirming?.text}</strong> will not affect any events that already
            copied it — those keep their own options. This only removes it from the library.
          </>
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setConfirming(null)}
      />

      <InfoDialog
        open={viewingId !== null && viewingState !== null && (viewingState.status === 'ready' || viewingState.status === 'error')}
        size="lg"
        title={
          viewingState?.status === 'error'
            ? 'Could not suggest scores'
            : viewingTemplate
              ? `Suggestions: ${viewingTemplate.text}`
              : 'AI Score Suggestions'
        }
        onClose={() => setViewingId(null)}
      >
        {viewingState?.status === 'error' ? (
          <p className="text-rose-600">{viewingState.message}</p>
        ) : viewingState?.status === 'ready' ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-600">
                {viewingState.data.events} event{viewingState.data.events === 1 ? '' : 's'}
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-600">
                {viewingState.data.total_leads} total leads
              </span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                {viewingState.data.won_leads} won
              </span>
            </div>

            {viewingState.data.won_leads < 5 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Low sample size ({viewingState.data.won_leads} won leads). Suggestions may be unreliable.
              </div>
            )}

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium text-black/40">
                  <th className="px-1 pb-2">Option</th>
                  <th className="px-1 pb-2 text-center">Current</th>
                  <th className="px-1 pb-2 text-center">Suggested</th>
                  <th className="px-1 pb-2">Why</th>
                </tr>
              </thead>
              <tbody>
                {viewingState.data.suggestions.map((s) => {
                  const current = viewingTemplate?.options.find((o) => o.label === s.label)?.score ?? 0
                  const changed = current !== s.score
                  return (
                    <tr key={s.label} className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-ink">{s.label}</td>
                      <td className="py-2 text-center text-black/40">{current}</td>
                      <td className={`py-2 text-center font-semibold ${changed ? 'text-midnight-green' : 'text-black/40'}`}>
                        {s.score}
                      </td>
                      <td className="py-2 pl-2 text-xs text-black/50">{s.reason}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {viewingState.data.insights.length > 0 && (
              <div>
                <h4 className="mb-1.5 font-display text-xs font-semibold text-ink">Insights</h4>
                <ul className="space-y-1">
                  {viewingState.data.insights.map((insight, i) => (
                    <li key={i} className="flex gap-2 text-xs text-black/60">
                      <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-midnight-green/50" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-black/50">{viewingState.data.summary}</p>

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => {
                  if (viewingId !== null) updateState(viewingId, { status: 'idle' })
                  setViewingId(null)
                }}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={applySuggestions}
                disabled={applying}
                className="rounded-md bg-ink px-4 py-2 font-display text-sm font-medium text-white transition hover:bg-midnight-green disabled:opacity-50"
              >
                {applying ? 'Applying…' : 'Apply & Save'}
              </button>
            </div>
          </div>
        ) : null}
      </InfoDialog>

      <InfoDialog size="lg" open={scoringInfoOpen} title="How lead scoring works" onClose={() => setScoringInfoOpen(false)}>
        <ScoringExplainer />
      </InfoDialog>
    </>
  )
}

function SuggestButton({
  state,
  onSuggest,
  onView,
}: {
  state: CardSuggestState
  onSuggest: () => void
  onView: () => void
}) {
  switch (state.status) {
    case 'loading':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-midnight-green/10 px-2 py-1 font-display text-xs font-medium text-midnight-green">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-midnight-green/30 border-t-midnight-green" />
          Analyzing…
        </span>
      )
    case 'ready':
      return (
        <button
          type="button"
          onClick={onView}
          className="inline-flex items-center gap-1 rounded-md bg-midnight-green px-2.5 py-1 font-display text-xs font-semibold text-white shadow-sm transition hover:bg-midnight-green/90 animate-pulse hover:animate-none"
        >
          <HiOutlineSparkles className="h-3.5 w-3.5" />
          View AI Suggestions
        </button>
      )
    case 'error':
      return (
        <button
          type="button"
          onClick={onView}
          className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2.5 py-1 font-display text-xs font-medium text-rose-600 transition hover:bg-rose-100"
        >
          Failed — view details
        </button>
      )
    default:
      return (
        <button
          type="button"
          onClick={onSuggest}
          className="inline-flex items-center gap-1 rounded-md bg-midnight-green/10 px-2 py-1 font-display text-xs font-medium text-midnight-green transition hover:bg-midnight-green/20"
        >
          <HiOutlineSparkles className="h-3.5 w-3.5" />
          AI Suggest Scores
        </button>
      )
  }
}
